// googleCalendarConnection.service.ts owns the three HTTP calls of the Google
// Calendar connection flow. Every one is a bff-web proxy to
// integrations-service, which owns the connection and the credential.

import { AxiosService } from 'src/axios/axios.service';
import { STATUS_FAILED_DEPENDENCY } from 'src/axios/axios.constants';
import type {
  tAgendaConnection,
  tAgendaConsent,
  tEnvelope,
} from './GoogleCalendarConnection.types';

const base = (workspaceId: string) =>
  `workspace/${workspaceId}/lead-agent/agenda/connection`;

/**
 * 424 is the CONTRACTUAL "no scheduling service in this environment" answer, so
 * it resolves instead of throwing — the same treatment the status panel gives it.
 * Without this every mount in an environment without the service would log an
 * error and capture it to Sentry.
 */
const allow424 = {
  isBFFService: true,
  validateStatus: (status: number) =>
    (status >= 200 && status < 300) || status === STATUS_FAILED_DEPENDENCY,
};

export const GoogleCalendarConnectionService = Object.freeze({
  /**
   * POST …/connection/consent — asks for the link that sends the owner to Google.
   *
   * Notably absent from the request: any capability flag. Whether this workspace
   * may use the agenda is decided server-side, and a value the browser sends is a
   * value the browser can change.
   */
  startConsent: async function (
    workspaceId: string,
    displayName?: string,
  ): Promise<tAgendaConsent | null> {
    const response = await AxiosService.post<
      { displayName?: string },
      tEnvelope<tAgendaConsent>
    >(`${base(workspaceId)}/consent`, { displayName }, allow424);

    if (!response?.success || !response.data) return null;
    return response.data;
  },

  /**
   * POST …/connection/sync — has the consent landed yet?
   *
   * Polled while the consent window is open, because the scheduling service has
   * no webhook to tell it. A `pending` answer is a success.
   */
  sync: async function (
    workspaceId: string,
  ): Promise<tAgendaConnection | null> {
    const response = await AxiosService.post<
      undefined,
      tEnvelope<tAgendaConnection>
    >(`${base(workspaceId)}/sync`, undefined, allow424);

    if (!response?.success || !response.data) return null;
    return response.data;
  },

  /** DELETE …/connection — revokes the workspace's connected calendar. */
  disconnect: async function (
    workspaceId: string,
  ): Promise<tAgendaConnection | null> {
    const response = await AxiosService.delete<tEnvelope<tAgendaConnection>>(
      base(workspaceId),
      allow424,
    );

    if (!response?.success || !response.data) return null;
    return response.data;
  },
});
